import TrashBin from '@components/icons/TrashBin.svg';
import { cn } from '@helpers/cn';
import { hasVsDeveloperRole } from '@helpers/hasVsDeveloperRole';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation } from '@tanstack/react-query';
import { PipelineAttemptsService } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import toast from 'react-hot-toast';
import { usePageContext } from '../../../../../../providers/PageProvider';

type TProps = {
  className?: string;
};

export const DeleteJobButton = ({ className }: TProps) => {
  const { me } = useAuthContext();
  const { job, manualReload, loadPipeline } = usePageContext();
  const { call } = useAPI();
  const { openModal, closeModal } = useModalContext();

  const toggleDeletedStateMutation = useMutation({
    mutationFn: ({ jobId, removed }: { jobId: string; removed: boolean }) =>
      call(() => PipelineAttemptsService.updateJobRemovedState({ jobId, requestBody: { removed } }), {
        onError: (message) => toast.error(message || 'Unable to update job removal state'),
      }),
    onSuccess: async (_response, { removed }) => {
      toast.success(removed ? 'Job marked as deleted' : 'Job restored');
      await loadPipeline();
      manualReload();
    },
  });
  const isLoading = toggleDeletedStateMutation.isPending;

  if (!job) {
    return null;
  }

  const isDeleted = Boolean(job.removed);
  const isVsDeveloper = hasVsDeveloperRole(me?.roles);

  if (isDeleted && !isVsDeveloper) {
    return 'Deleted';
  }
  const label = isDeleted ? 'Undelete' : 'Delete';

  const confirmToggle = () => {
    openModal(ModalNamesEnum.GenericConfirmation, {
      title: isDeleted ? 'Undelete job' : 'Delete job',
      description: isDeleted ? 'This will mark the job as active again.' : 'This will mark the job as deleted.',
      buttonText: label,
      onCancel: () => closeModal(),
      onSuccess: () => {
        closeModal();
        toggleDeletedStateMutation.mutate({ jobId: job.id, removed: !isDeleted });
      },
    });
  };

  return (
    <div
      role="button"
      aria-label={`${label} job`}
      aria-disabled={isLoading}
      onClick={isLoading ? undefined : confirmToggle}
      className={cn(
        'min-w-[110px] flex gap-1 text-[var(--color-text-muted)] hover:text-[var(--color-title)]',
        isLoading && 'opacity-60 cursor-not-allowed',
        className,
      )}
      title={`${label} job`}
    >
      <TrashBin width={22} height={22} />
      {label}
    </div>
  );
};
